import { useEffect, useMemo, useState, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import ImageUpload from "@/components/admin/ImageUpload";
import { supabase } from "@/lib/supabase";
import { detectColumns, safeInsert, safeUpdate } from "@/lib/schema";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  MagnifyingGlass,
  Plus,
  Pencil,
  Trash,
  ArrowClockwise,
  CheckCircle,
  Prohibit,
  Package,
  Star,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const EMPTY = {
  id: null,
  name: "",
  slug: "",
  sku: "",
  short_description: "",
  description: "",
  category: "",
  price: 0,
  compare_at_price: 0,
  currency: "INR",
  stock: 0,
  image: "",
  cover_image: "",
  thumbnail: "",
  is_active: true,
  is_featured: false,
};

const fmtInr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function AdminProducts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("products")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(500);
    if (error) toast.error(error.message);
    setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel("admin-products")
      .on("postgres_changes", { event: "*", schema: "public", table: "products" }, () => load())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows.filter((p) => {
      const active = p.is_active !== false;
      if (statusFilter === "active" && !active) return false;
      if (statusFilter === "inactive" && active) return false;
      if (statusFilter === "featured" && !p.is_featured) return false;
      if (!query) return true;
      return `${p.name || ""} ${p.slug || ""} ${p.sku || ""} ${p.category || ""}`
        .toLowerCase()
        .includes(query);
    });
  }, [rows, q, statusFilter]);

  const openNew = () => setEditing({ ...EMPTY });
  const openEdit = (p) => setEditing({ ...EMPTY, ...p });

  const persist = async (payload) => {
    if (!payload.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const full = {
        name: payload.name,
        slug: payload.slug || slugify(payload.name),
        sku: payload.sku,
        short_description: payload.short_description,
        description: payload.description,
        category: payload.category,
        price: Number(payload.price) || 0,
        compare_at_price: Number(payload.compare_at_price) || 0,
        currency: payload.currency || "INR",
        stock: Number(payload.stock) || 0,
        image: payload.image,
        cover_image: payload.image,
        thumbnail: payload.image,
        is_active: payload.is_active !== false,
        is_featured: !!payload.is_featured,
      };
      const res = payload.id
        ? await safeUpdate("products", full, { id: payload.id })
        : await safeInsert("products", full);
      if (res.error) throw res.error;
      toast.success(payload.id ? "Product updated" : "Product created");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This will remove it from the storefront immediately.`)) return;
    // Optimistic UI: yank it out of the local list first
    setRows((prev) => prev.filter((r) => r.id !== p.id));
    const { error } = await supabase.from("products").delete().eq("id", p.id);
    if (error) {
      toast.error(`Delete failed: ${error.message}`);
      load(); // restore the row we optimistically removed
      return;
    }
    toast.success(`"${p.name}" deleted from storefront`);
  };

  const toggleActive = async (p) => {
    const next = !(p.is_active !== false);
    const cols = await detectColumns("products");
    if (!cols.has("is_active") && cols.size > 0) return toast.error("No is_active column");
    const { error } = await supabase.from("products").update({ is_active: next }).eq("id", p.id);
    if (error) toast.error(error.message);
    else {
      toast.success(next ? "Activated" : "Deactivated");
      load();
    }
  };

  const toggleFeatured = async (p) => {
    const next = !p.is_featured;
    const cols = await detectColumns("products");
    if (!cols.has("is_featured") && cols.size > 0) return toast.error("No is_featured column");
    const { error } = await supabase.from("products").update({ is_featured: next }).eq("id", p.id);
    if (error) toast.error(error.message);
    else {
      toast.success(next ? "Marked featured" : "Unmarked featured");
      load();
    }
  };

  return (
    <AdminLayout title="Products">
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-5">
        <div className="flex-1 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-sm min-w-[200px]">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="products-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, SKU, category…"
              className="pl-9 h-10 rounded-xl bg-white"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="products-filter" className="w-44 h-10 rounded-xl bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="featured">Featured</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} className="rounded-xl h-10 border-slate-200">
            <ArrowClockwise size={14} weight="bold" className="mr-1.5" /> Refresh
          </Button>
          <Button onClick={openNew} data-testid="products-add" className="rounded-xl h-10 bg-navy-deep hover:bg-navy text-white">
            <Plus size={14} weight="bold" className="mr-1.5" /> Add Product
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">{[0,1,2,3,4].map((i) => <div key={i} className="h-14 rounded-xl bg-slate-100 animate-pulse" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">No products found. Click <b>Add Product</b>.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500 font-mono-data">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Image</th>
                  <th className="text-left px-5 py-3 font-semibold">Name</th>
                  <th className="text-left px-5 py-3 font-semibold">SKU</th>
                  <th className="text-left px-5 py-3 font-semibold">Category</th>
                  <th className="text-left px-5 py-3 font-semibold">Price</th>
                  <th className="text-left px-5 py-3 font-semibold">Stock</th>
                  <th className="text-left px-5 py-3 font-semibold">Status</th>
                  <th className="text-right px-5 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const active = p.is_active !== false;
                  const img = p.image || p.cover_image || p.thumbnail;
                  return (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-5 py-3">
                        {img ? <img src={img} alt="" className="h-10 w-10 rounded-lg object-cover border border-slate-200" /> :
                          <div className="h-10 w-10 rounded-lg bg-slate-100 border border-slate-200 grid place-items-center text-slate-400"><Package size={16} /></div>}
                      </td>
                      <td className="px-5 py-3 text-navy-deep font-semibold flex items-center gap-1.5">
                        {p.name}
                        {p.is_featured && <Star size={12} weight="fill" className="text-amber-500" />}
                      </td>
                      <td className="px-5 py-3 text-slate-500 font-mono-data text-[12px]">{p.sku || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{p.category || "—"}</td>
                      <td className="px-5 py-3 font-semibold text-navy-deep tabular-nums">{fmtInr(p.price)}</td>
                      <td className={`px-5 py-3 font-mono-data tabular-nums ${(p.stock ?? 0) < 5 ? "text-red-500 font-bold" : "text-slate-600"}`}>{p.stock ?? "—"}</td>
                      <td className="px-5 py-3">
                        <span className={`text-[10.5px] uppercase tracking-[0.14em] font-mono-data font-semibold px-2 py-1 rounded-full ${active ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500"}`}>{active ? "Active" : "Inactive"}</span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => toggleFeatured(p)} className={`h-8 w-8 rounded-lg grid place-items-center ${p.is_featured ? "text-amber-500 hover:bg-amber-50" : "text-slate-500 hover:text-amber-500 hover:bg-slate-100"}`} title="Toggle featured">
                            <Star size={14} weight={p.is_featured ? "fill" : "regular"} />
                          </button>
                          <button onClick={() => toggleActive(p)} className="h-8 w-8 rounded-lg grid place-items-center text-slate-500 hover:text-cyan-brand hover:bg-slate-100" title={active ? "Deactivate" : "Activate"}>
                            {active ? <Prohibit size={14} /> : <CheckCircle size={14} />}
                          </button>
                          <button onClick={() => openEdit(p)} className="h-8 w-8 rounded-lg grid place-items-center text-slate-500 hover:text-cyan-brand hover:bg-slate-100" title="Edit"><Pencil size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <ProductEditor value={editing} onChange={setEditing} onSave={() => persist(editing)} onCancel={() => setEditing(null)} saving={saving} />}
    </AdminLayout>
  );
}

function ProductEditor({ value, onChange, onSave, onCancel, saving }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const isEdit = !!value.id;
  const submit = (e) => { e.preventDefault(); onSave(); };

  return (
    <Dialog open onOpenChange={(o) => (o ? null : onCancel())}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto p-0">
        <DialogHeader className="p-6 border-b border-slate-200">
          <DialogTitle className="font-display text-2xl text-navy-deep">{isEdit ? "Edit product" : "New product"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Name <span className="text-red-500">*</span></Label>
              <Input value={value.name} onChange={(e) => { const name = e.target.value; set({ name, slug: value.slug || slugify(name) }); }} required className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Slug</Label>
              <Input value={value.slug} onChange={(e) => set({ slug: slugify(e.target.value) })} className="mt-1.5 h-11 rounded-xl font-mono-data text-[13px]" />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">SKU</Label>
              <Input value={value.sku || ""} onChange={(e) => set({ sku: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Category</Label>
              <Input value={value.category || ""} onChange={(e) => set({ category: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Currency</Label>
              <Input value={value.currency || "INR"} onChange={(e) => set({ currency: e.target.value.toUpperCase() })} className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Price</Label>
              <Input type="number" step="0.01" value={value.price} onChange={(e) => set({ price: Number(e.target.value) })} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Compare at price</Label>
              <Input type="number" step="0.01" value={value.compare_at_price} onChange={(e) => set({ compare_at_price: Number(e.target.value) })} className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Stock</Label>
              <Input type="number" value={value.stock} onChange={(e) => set({ stock: Number(e.target.value) })} className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Short description</Label>
            <Input value={value.short_description || ""} onChange={(e) => set({ short_description: e.target.value })} className="mt-1.5 h-11 rounded-xl" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-[0.12em] text-navy-deep font-semibold">Description</Label>
            <Textarea value={value.description || ""} onChange={(e) => set({ description: e.target.value })} rows={5} className="mt-1.5 rounded-xl" />
          </div>
          <ImageUpload value={value.image} onChange={(url) => set({ image: url })} folder="products" label="Product Image" />
          <div className="flex gap-4">
            <label className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white border border-slate-200 cursor-pointer flex-1">
              <input type="checkbox" checked={value.is_active !== false} onChange={(e) => set({ is_active: e.target.checked })} className="h-4 w-4" />
              <span className="text-[13px] font-semibold text-navy-deep">Active</span>
            </label>
            <label className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white border border-slate-200 cursor-pointer flex-1">
              <input type="checkbox" checked={!!value.is_featured} onChange={(e) => set({ is_featured: e.target.checked })} className="h-4 w-4" />
              <span className="text-[13px] font-semibold text-navy-deep">Featured</span>
            </label>
          </div>
          <div className="pt-4 border-t border-slate-200 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} className="rounded-full h-11 px-5 border-slate-200">Cancel</Button>
            <Button type="submit" disabled={saving} className="rounded-full h-11 px-5 bg-cta-gradient text-white font-semibold">
              {saving ? "Saving…" : isEdit ? "Update" : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
